'use client'

import { useEffect, useState } from 'react'

import { headersOf, isUserActivity } from './activity'

/** Below this a request is fast enough that a bar would only flicker. */
const SHOW_AFTER_MS = 150

type Phase = 'idle' | 'running' | 'done'

/**
 * A thin bar along the top of the page while something a person asked for is
 * loading: a page of the dashboard or of a module, or a server action. It
 * appears only after SHOW_AFTER_MS, creeps towards the end while the request
 * runs and closes when the last one has answered.
 *
 * It watches window.fetch, which the Next router calls for both. That way the
 * host and every module app show the same bar without a line of module code.
 */
export function ActivityBar() {
  const [phase, setPhase] = useState<Phase>('idle')

  useEffect(() => {
    let lastInput = -Infinity
    let running = 0
    let showTimer: ReturnType<typeof setTimeout> | undefined
    let hideTimer: ReturnType<typeof setTimeout> | undefined

    const noteInput = () => {
      lastInput = performance.now()
    }
    const begin = () => {
      running += 1
      if (running > 1) return
      clearTimeout(hideTimer)
      setPhase(current => (current === 'done' ? 'idle' : current))
      showTimer = setTimeout(() => setPhase('running'), SHOW_AFTER_MS)
    }
    const end = () => {
      running = Math.max(0, running - 1)
      if (running > 0) return
      clearTimeout(showTimer)
      setPhase(current => (current === 'running' ? 'done' : current))
      hideTimer = setTimeout(() => setPhase('idle'), 400)
    }

    const original = window.fetch
    const watched: typeof fetch = (input, init) => {
      if (!isUserActivity(headersOf(input, init), performance.now() - lastInput)) return original(input, init)
      begin()
      return original(input, init).finally(end)
    }
    window.fetch = watched
    window.addEventListener('pointerdown', noteInput, true)
    window.addEventListener('keydown', noteInput, true)
    return () => {
      if (window.fetch === watched) window.fetch = original
      window.removeEventListener('pointerdown', noteInput, true)
      window.removeEventListener('keydown', noteInput, true)
      clearTimeout(showTimer)
      clearTimeout(hideTimer)
    }
  }, [])

  return (
    <div aria-hidden className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-0.5">
      <div
        className={
          phase === 'idle'
            ? 'h-full w-0 bg-primary opacity-0'
            : phase === 'running'
              ? 'h-full w-[85%] bg-primary opacity-100 transition-[width] duration-[8000ms] ease-out motion-reduce:transition-none'
              : 'h-full w-full bg-primary opacity-0 transition-[width,opacity] duration-300 ease-out'
        }
      />
    </div>
  )
}
